import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface CoinbaseCheckout {
  id: string;
  url: string;
  status: string;
  amount: string;
  currency: string;
  description?: string;
  network: string;
  address: string;
  tokenAddress?: string;
  expiresAt?: string;
  metadata: Record<string, unknown> | null;
  settlement?: {
    totalAmount: string;
    feeAmount: string;
    netAmount: string;
    currency: string;
  };
  fiatAmount?: string;
  fiatCurrency?: string;
  refundedAmount?: string;
  createdAt: string;
  updatedAt: string;
}

interface ActionTestResponse {
  data?: {
    result?: {
      success?: boolean;
      checkouts?: CoinbaseCheckout[];
      error?: {
        code: string;
        message: string;
      };
    };
  };
  error?: {
    message: string;
  };
}

const PAYMENT_EVENT_STATUSES: Record<string, string> = {
  completed: "success",
  failed: "failed",
  expired: "expired",
};

const REFUND_EVENT_STATUSES: Record<string, string> = {
  refunded: "success",
  partially_refunded: "success",
};

function mapCheckoutStatus(status: string): { prefix: string; suffix: string } | null {
  const lower = status.toLowerCase();
  const paymentSuffix = PAYMENT_EVENT_STATUSES[lower];
  if (paymentSuffix) return { prefix: "checkout.payment", suffix: paymentSuffix };
  const refundSuffix = REFUND_EVENT_STATUSES[lower];
  if (refundSuffix) return { prefix: "checkout.refund", suffix: refundSuffix };
  return null;
}

/**
 * Fetch recent checkouts from Coinbase Business via the
 * coinbase-business-list-checkouts action.
 *
 * Used as fallback data for the coinbase-checkout-received trigger
 * in both draft and live modes.
 *
 * Requires: Coinbase Business connector with API key credentials.
 *
 * Note: uses /v1/actions/.../test instead of /v1/action-proxy/.../query
 * because the action-proxy endpoint does not forward connector credentials.
 */
export const coinbaseCheckoutFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { connectorId } = ctx;

  if (!connectorId) {
    throw new Error("No Coinbase Business connector linked. Add a Coinbase Business connector to test this trigger.");
  }

  const response = await authenticatedFetch<ActionTestResponse>(
    `${V2_ENDPOINT}/v1/actions/coinbase-business-list-checkouts/test`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        inputs: { pageSize: 10 },
        connector: { id: connectorId, type: "coinbase-business" },
      }),
      signal: AbortSignal.timeout(30_000),
    },
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Failed to fetch checkouts from Coinbase (HTTP ${response.status}): ${text}`);
  }

  const json = await response.json();

  const result = json.data?.result;
  if (result?.success === false) {
    const msg = result.error?.message || JSON.stringify(result.error ?? result);
    throw new Error(`Coinbase action failed: ${msg}`);
  }

  if (json.error?.message) {
    throw new Error(json.error.message);
  }

  const checkouts: CoinbaseCheckout[] = result?.checkouts ?? [];
  if (checkouts.length === 0) return [];

  return checkouts.flatMap(checkout => {
    const mapped = mapCheckoutStatus(checkout.status);
    if (!mapped) return [];
    return [
      {
        triggeredAt: checkout.updatedAt || checkout.createdAt,
        eventType: `${mapped.prefix}.${mapped.suffix}`,
        checkout: {
          id: checkout.id,
          url: checkout.url,
          status: checkout.status,
          amount: checkout.amount,
          currency: checkout.currency,
          description: checkout.description,
          network: checkout.network,
          address: checkout.address,
          tokenAddress: checkout.tokenAddress,
          expiresAt: checkout.expiresAt,
          metadata: checkout.metadata,
          settlement: checkout.settlement,
          fiatAmount: checkout.fiatAmount,
          fiatCurrency: checkout.fiatCurrency,
          refundedAmount: checkout.refundedAmount,
          createdAt: checkout.createdAt,
          updatedAt: checkout.updatedAt,
        },
        // Display metadata (stripped before execution)
        _amount: `${checkout.amount} ${checkout.currency}`,
        _status: checkout.status.toLowerCase(),
        _description: checkout.description || "No description",
        _network: checkout.network || "base",
        _createdAt: checkout.createdAt,
      },
    ];
  });
};
